import { Link } from "react-router-dom";
import signalLogo from "@/assets/signal-logo-on-dark.svg";
import { useSupportChat } from "@/components/SupportChatProvider";
import { EXTERNAL_LINK_PROPS, PRIVACY_POLICY_URL } from "@/lib/legal";

// Shared footer bar: the signal lockup + legal identity on the left and the
// legal/support links (divider-separated) on the right. Sized + padded to match
// the header bar. Used by both the marketing shell (AppShell) and the
// authenticated shell (PortalShell) so the identity block, the `build-info`
// marker, and the links live in one place (DRY). The version is inlined at build
// time via __APP_VERSION__. Contact Support opens live chat when it is
// configured (BRK-S136) and is left inert when it isn't.
//
// In the authenticated shell it is `sticky` (BRK-S228): from lg up it stays at
// the bottom of the viewport, at the fixed `--brk-footer-h` the sidebar is sized
// against. Below lg it stays at the end of the page, leaving the bottom of a
// phone to the form action dock (BRK-S198). The marketing shell's footer is
// not sticky.
//
// The legal links (BRK-S149) are the reason the footer is the right home for
// them: it is the only chrome both shells share, so /terms is reachable from
// every screen, signed out and signed in. A link is in-app (`to`) or off-origin
// (`href`); one with neither is still presentational, and — unlike the nav rows
// — deliberately does NOT fall through to live chat (BRK-S136).
const LINKS: Array<{ label: string; to?: string; href?: string }> = [
  // ONE privacy policy for the whole entity, on the marketing site. Linked,
  // never restated here — see src/lib/legal.ts.
  { label: "Privacy Policy", href: PRIVACY_POLICY_URL },
  { label: "Terms of Service", to: "/terms" },
  { label: "Security" },
];

const LINK_CLASS = "transition-colors hover:text-white";

export function SiteFooter({ sticky = false }: { sticky?: boolean }) {
  const year = new Date().getFullYear();
  const { enabled: chatEnabled, open: openChat } = useSupportChat();

  return (
    <footer
      className={`border-t border-white/10 bg-brk-ink text-sm text-white/60 ${
        sticky ? "lg:sticky lg:bottom-0 lg:z-30 lg:h-[var(--brk-footer-h)]" : ""
      }`}
    >
      <div
        className={`flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-6 ${
          sticky ? "lg:h-full lg:py-0" : ""
        }`}
      >
        {/* Left: logo + legal identity. */}
        <div className="flex items-center gap-3">
          <img src={signalLogo} alt="" className="h-[1.6rem] w-auto shrink-0" />
          <div className="flex flex-col text-[0.7rem] leading-tight">
            <span>© {year} Signal Specialty Insurance Services, LLC</span>
            <span className="text-white/50">
              25 Bridge St, Red Bank NJ, 07701
              <span data-testid="build-info" className="ml-2 text-white/30">
                v{__APP_VERSION__}
              </span>
            </span>
          </div>
        </div>

        {/* Right: divider-separated links. */}
        <nav aria-label="Footer" className="flex items-center">
          {LINKS.map(({ label, to, href }, i) => (
            <span key={label} className="flex items-center">
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className="mx-4 h-4 w-px bg-white/20"
                />
              )}
              {to ? (
                <Link to={to} className={LINK_CLASS}>
                  {label}
                </Link>
              ) : href ? (
                <a href={href} {...EXTERNAL_LINK_PROPS} className={LINK_CLASS}>
                  {label}
                </a>
              ) : (
                <button type="button" className={LINK_CLASS}>
                  {label}
                </button>
              )}
            </span>
          ))}
          <span aria-hidden="true" className="mx-4 h-4 w-px bg-white/20" />
          <button
            type="button"
            onClick={chatEnabled ? openChat : undefined}
            className="inline-flex items-center gap-1.5 font-medium text-brk-accent transition-colors hover:text-brk-accent-strong"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 13a8 8 0 0 1 16 0M4 13v3a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 1Zm16 0v3a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 1Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Contact Support
          </button>
        </nav>
      </div>
    </footer>
  );
}
